import { useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useUpdateAgent } from "@/hooks/agent";
import { useUpdateDocument } from "@/hooks/documents/useDocumentMutations";
import { useProfiles } from "@/hooks/auth/useProfiles";
import { ConversationService } from "@/services/conversation/ConversationService";
import { Profile, Roles } from '@/types/auth/auth';
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface AssignToCustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agent: any;
  onAssignmentUpdate: () => void;
}

export const AssignToCustomerDialog = ({ isOpen, onClose, agent, onAssignmentUpdate }: AssignToCustomerDialogProps) => {
  const [filteredCustomers, setFilteredCustomers] = useState<Profile[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [reassignDocs, setReassignDocs] = useState(true);
  const [showClearDataPrompt, setShowClearDataPrompt] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);

  const queryClient = useQueryClient();
  const { data: customers = [], isLoading } = useProfiles();
  const updateDocumentMutation = useUpdateDocument();

  useEffect(() => {
    if (isOpen && agent) {
      setSelectedCustomer(agent.user_id || "");
      setReassignDocs(true);
    }
  }, [isOpen, agent]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(
        (customer) =>
          (customer.full_name && customer.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  const updateAgentMutation = useUpdateAgent();

  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomer(customerId);
  };

  const isReassignment = !!agent?.user_id && !!selectedCustomer && selectedCustomer !== agent.user_id;

  const handleSave = async () => {
    if (isReassignment) {
      setShowClearDataPrompt(true);
      return;
    }
    await performSave(false);
  };

  const performSave = async (clearPriorData: boolean) => {
    try {
      await updateAgentMutation.mutateAsync({
        agent,
        updateData: { user_id: selectedCustomer || null },
      });

      if (reassignDocs && selectedCustomer) {
        const kbDocs: DocumentWithMetadata[] = agent.knowledge_base || [];
        await Promise.all(
          kbDocs.map((doc) =>
            updateDocumentMutation.mutateAsync({
              document: doc,
              updates: { ...doc, user_id: selectedCustomer },
            })
          )
        );
      }

      if (clearPriorData) {
        setIsClearingData(true);
        await ConversationService.clearAgentData(agent.id);
      }

      toast.success("Customer assignment updated successfully");
      onAssignmentUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    } finally {
      setIsClearingData(false);
      setShowClearDataPrompt(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>Assign "{agent?.name}" to Customer</SheetTitle>
          <SheetDescription>
            Select which customer should have access to this agent. Only the selected customer will be able to interact with this agent.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search customers by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto border rounded-lg p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading customers...</span>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? "No customers found matching your search" : "No customers found"}
              </div>
            ) : (
              <RadioGroup value={selectedCustomer} onValueChange={handleSelectCustomer}>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="" id="unassigned" />
                    <Label htmlFor="unassigned" className="flex-1 cursor-pointer">
                      <div className="font-medium">Unassigned</div>
                      <div className="text-sm text-gray-500">No specific customer assigned</div>
                    </Label>
                  </div>
                  {filteredCustomers.map((customer) => {
                    const displayName = customer.full_name || customer.email || `User ${customer.id.slice(0, 8)}...`;
                    const hasNoName = !customer.full_name && !customer.email;

                    return (
                      <div key={customer.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50">
                        <RadioGroupItem value={customer.id} id={customer.id} />
                        <Label htmlFor={customer.id} className="flex-1 cursor-pointer">
                          <div className="font-medium">
                            <CopyableEmail email={displayName} />
                            {hasNoName && <span className="text-xs text-orange-600 ml-2">(No name set)</span>}
                          </div>
                          {customer.email && customer.full_name && <CopyableEmail email={customer.email} className="text-sm text-gray-500" />}
                          {customer.role && (
                            <div className="text-xs text-gray-400">
                              Role:{" "}
                              {{
                                [Roles.Admin]: "Admin",
                                [Roles.Business]: "Business",
                                [Roles.Consumer]: "Consumer",
                              }[customer.role] || customer.role}
                            </div>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            )}
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm font-medium text-gray-700 mb-1">Current Selection:</div>
            <div className="text-sm text-gray-600">
              {selectedCustomer ? (
                (() => {
                  const customer = customers.find((c) => c.id === selectedCustomer);
                  if (customer) {
                    const displayName = customer.full_name || customer.email || `User ${customer.id.slice(0, 8)}...`;
                    return (
                      <>
                        <CopyableEmail email={displayName} className="font-medium" />
                        {customer.email && customer.full_name && <span className="text-gray-500"> (<CopyableEmail email={customer.email} />)</span>}
                      </>
                    );
                  }
                  return <span>Customer ID: {selectedCustomer}</span>;
                })()
              ) : (
                <span className="text-gray-500 italic">No customer selected - Agent will be unassigned</span>
              )}
            </div>
          </div>
        </div>

        {selectedCustomer && (agent.knowledge_base?.length ?? 0) > 0 && (
          <div className="px-1 pb-4">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Checkbox
                id="reassign-docs"
                checked={reassignDocs}
                onCheckedChange={(v) => setReassignDocs(!!v)}
                className="mt-0.5"
              />
              <Label htmlFor="reassign-docs" className="cursor-pointer text-sm text-blue-900 leading-snug">
                Also reassign {agent.knowledge_base.length} knowledge base document{agent.knowledge_base.length !== 1 ? "s" : ""} attached to this genie to the selected user
              </Label>
            </div>
          </div>
        )}

        <SheetFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateAgentMutation.isPending || updateDocumentMutation.isPending || isClearingData}
          >
            {(updateAgentMutation.isPending || updateDocumentMutation.isPending || isClearingData) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Assignment
          </Button>
        </SheetFooter>
      </SheetContent>

      <AlertDialog open={showClearDataPrompt} onOpenChange={(open) => !isClearingData && setShowClearDataPrompt(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear prior conversations and activity?</AlertDialogTitle>
            <AlertDialogDescription>
              "{agent?.name}" is being reassigned to a different customer. If this genie was used for testing or by a
              previous customer, its existing conversations, activity log entries, and inbox data may not be
              appropriate for the new customer to see. Do you want to clear this history now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearingData} onClick={() => performSave(false)}>
              Keep history
            </AlertDialogCancel>
            <AlertDialogAction disabled={isClearingData} onClick={() => performSave(true)}>
              {isClearingData && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Clear history
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
};
